import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import { CompanyRole, TCompanyCreateRequestPayload, TCompanyResponse } from "./companies.types";

import { handleAsync } from "@/utils";
import { TApiResponse } from "../../types/api";
import { withAuthHooks } from "../auth/auth.ky.hooks";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class CompaniesService {
  private rootEndpoint = "companies" as const;

  getCurrentUserCompanies(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyResponse[]>>(() =>
      apiService.get(this.rootEndpoint.concat("/me"), options)
    );
  }

  createCompany(payload: TCompanyCreateRequestPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyResponse>>(() =>
      apiService.post(this.rootEndpoint.concat("/"), payload, options)
    );
  }

  getCompany(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/${companyId}`, options)
    );
  }

  updateCompany(companyId: string, payload: { role?: CompanyRole }, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyResponse>>(() =>
      apiService.patch(`${this.rootEndpoint}/${companyId}`, payload, options)
    );
  }

  completeOnboarding(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyResponse>>(() =>
      apiService.post(`${this.rootEndpoint}/${companyId}/complete-onboarding`, undefined, options)
    );
  }

  getOnboardingData(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<{
        company: TCompanyResponse;
        sections: {
          id: string;
          name: string;
          description: string | null;
          role_type: CompanyRole | null;
          order: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        }[];
        onboarding_progress: number;
        completed_steps: string[];
        pending_steps: string[];
      }>
    >(() => apiService.get(`${this.rootEndpoint}/${companyId}/onboarding-data`, options));
  }

  getCompanyStats(options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<{
        total_companies: number;
        completed_onboarding: number;
        pending_onboarding: number;
        completion_rate: number;
        companies_by_role: {
          fundraiser: number;
          investor: number;
        };
      }>
    >(() => apiService.get(`${this.rootEndpoint}/stats/overview`, options));
  }
}

export const companiesService = new CompaniesService();
export type { TCompanyResponse };
